import {
  AccountAlreadyExistsSmsError,
  ContactIdAlreadyInUseError,
  InvalidCharacterContactIdError,
  InvalidCharacterEmailError,
  InvalidCharacterFirstNamesError,
  InvalidCharacterLastNamesError,
  InvalidEmailError,
  InvalidFirstNamesError,
  InvalidLastNamesError,
  InvalidSmsNumberError,
  MobileNumberAlreadyInUseError,
  MobileNumberInvalidError,
} from '../errors'

// Map User Profile API error codes to ValidationError classes
// Codes 302, 304, 306, 310 are path-dependent (fullName fields vs contactId vs contactInfo)
// Code 312 is path-independent
const errorCodeMap = {
  302: {
    // ALREADY_IN_USE (CIS 179 — contact name already exists)
    requiresPath: true,
    contactId: ContactIdAlreadyInUseError,
  },
  304: {
    // FIELD_REQUIRED
    requiresPath: true,
    givenName: InvalidFirstNamesError,
    surname: InvalidLastNamesError,
  },
  306: {
    // INVALID_VALUE
    requiresPath: true,
    givenName: InvalidCharacterFirstNamesError,
    surname: InvalidCharacterLastNamesError,
    contactId: InvalidCharacterContactIdError,
    email: InvalidEmailError,
    phone: MobileNumberInvalidError,
  },
  310: {
    // PUBLIC_FIELD_NOT_ALLOWED
    requiresPath: true,
    givenName: InvalidFirstNamesError,
    surname: InvalidLastNamesError,
  },
  312: InvalidFirstNamesError, // UPDATE_NOT_ALLOWED - path-independent
}

// CIS-style warning codes used by profile service for contact info validation
const warningCodeToError = {
  163: () => new InvalidEmailError(),
  169: () => new InvalidCharacterEmailError(),
  173: () => new MobileNumberInvalidError(),
  174: () => new InvalidSmsNumberError(),
  177: () => new MobileNumberAlreadyInUseError(),
  262: () => new AccountAlreadyExistsSmsError([]),
}

export default function throwUserProfileError(error) {
  // Check for CIS-style warning header codes (used by profile service for contact info)
  const warningCode = (error?.response?.headers?.warning ?? '').match(/\d+/)?.[0]
  if (warningCode) {
    const createWarningError = warningCodeToError[warningCode]
    if (createWarningError) {
      throw createWarningError()
    }
  }

  // Parse User Profile API response body format
  const bodyError = findBodyError(error)
  if (bodyError) {
    const ErrorClass = mapErrorCodeAndPath(bodyError.code, bodyError.path)
    if (ErrorClass) {
      throw new ErrorClass()
    }
  }

  // No mapped error found, rethrow original
  throw error
}

function findBodyError(error) {
  // Parse User Profile API response body format
  // Returns first error from errors array
  const errors = error?.response?.data?.errors
  if (Array.isArray(errors) && errors.length > 0) {
    return {
      code: errors[0].code,
      message: errors[0].message,
      path: errors[0].path,
    }
  }
  return null
}

function mapErrorCodeAndPath(code, path) {
  const mapping = errorCodeMap[code]

  // Unknown error code
  if (!mapping) {
    return null
  }

  // Path-independent error (e.g., code 312)
  if (typeof mapping === 'function') {
    return mapping
  }

  // Path-dependent error requires path to determine field
  if (mapping.requiresPath && !path) {
    return null
  }

  // Determine field from path
  // Examples: "fullName.givenName" → givenName, "fullName.surname" → surname, "contactId" → contactId
  if (path?.includes('givenName')) return mapping.givenName
  if (path?.includes('surname')) return mapping.surname
  if (mapping.contactId && path?.includes('contactId')) return mapping.contactId
  if (mapping.email && path?.toLowerCase().includes('email')) return mapping.email
  if (mapping.phone && (path?.toLowerCase().includes('phone') || path?.toLowerCase().includes('mobile')))
    return mapping.phone

  // Unknown path — don't map to wrong field
  return null
}
